//groupedInputs.tsx
import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import Node, { contentStyle as style } from './Node';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { GroupedNodeProps, Input, GroupedInputs } from './types';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import Chip from '@mui/material/Chip';
import { useStore } from './store';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Popover from '@mui/material/Popover';


const GroupedNode: React.FC<GroupedNodeProps> = ({ id, data, selected = false, onInputDoubleClick }) => {
    const { overallTotal, setDollarValue, calculateOverallTotal, parentChildMap, updateParentSubInputValue, calculateNodeTotal, setParentChildRelation } = useStore();

    const { dollarValues, nodeTotals } = useStore(state => ({
        dollarValues: state.dollarValues,
        nodeTotals: state.nodeTotals
    }));

    const { selectedYear, selectedMonth } = useStore(state => ({
        selectedYear: state.selectedYear,
        selectedMonth: state.selectedMonth,
    }));


    const { priceHistory, electricityPrices } = useStore(state => ({
        priceHistory: state.priceHistory,
        electricityPrices: state.electricityPrices,
    }));

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);


    const groupInputsByFlowID = (inputs: Input[]): GroupedInputs => {
        return inputs.reduce((grouped, input) => {
            const flowID = input.InputFlowID.low.toString();
            if (!grouped[flowID]) {
                grouped[flowID] = [];
            }
            grouped[flowID].push(input);
            return grouped;
        }, {} as GroupedInputs);
    };

    const nodeID = `parent-${data.startingflowid}-${data.processid}`;


    const organizeInputsByFlowType = () => {
        const productFlowInputs = data.inputs.filter(input => input.FlowType === 'PRODUCT_FLOW' && input.DefaultProvider.low !== 0);
        const elementaryFlowInputs = data.inputs.filter(input => input.FlowType === 'ELEMENTARY_FLOW');
        return {
            PRODUCT_FLOW: groupInputsByFlowID(productFlowInputs),
            ELEMENTARY_FLOW: groupInputsByFlowID(elementaryFlowInputs),
        };
    };

    const organizedInputs = React.useMemo(() => organizeInputsByFlowType(), [data.inputs]);


    const RenderInput = (input: Input, key: string, isGrouped: boolean = false) => {

        // Local state for handling user input immediately
        const [localDollarPerUnit, setLocalDollarPerUnit] = useState('');




        useEffect(() => {
            let latestPriceValue: string = ''; // Explicitly type latestPriceValue as string

            // Define fetchElectricityPrice with explicit parameter type
            const fetchElectricityPrice = (code: string): string => {
                const electricityPricesForCode = electricityPrices[code] || [];
                const filteredPrices = electricityPricesForCode.filter(price =>
                    price.REFYEAR < selectedYear || (price.REFYEAR === selectedYear && price.REFMONTH <= selectedMonth)
                );
                // Corrected sort function using .getTime()
                const latestPrice = filteredPrices.sort((a, b) =>
                    new Date(b.REFYEAR, b.REFMONTH - 1).getTime() - new Date(a.REFYEAR, a.REFMONTH - 1).getTime()
                )[0];
                return latestPrice ? latestPrice.AVG_VALUE_IN_USD.toString() : '';
            };



            // Transport type specific values
            const transportTypePrices: { [key: string]: string } = {
                "transport, freight train": "0.03",
                "transport, freight, lorry": "0.20",
                "transport, freight, inland waterways": "0.01",
                "transport, freight, sea": "0.01"
            };

            const fetchPriceHistory = (hscode: string): string => {
                const historyItems = priceHistory[hscode] || [];
                const filteredHistory = historyItems.filter(item =>
                    item.REFYEAR < selectedYear || (item.REFYEAR === selectedYear && item.REFMONTH <= selectedMonth)
                );
                const latestHistoryItem = filteredHistory.sort((a, b) =>
                    new Date(b.REFYEAR, b.REFMONTH).getTime() - new Date(a.REFYEAR, a.REFMONTH).getTime()
                )[0];
                let value = 0;
                if (latestHistoryItem) {
                    value = parseFloat(latestHistoryItem.AVERAGEUNITVALUE.toString());
                }
                return value.toString();
            }

            // Check for specific transport types in InputFlowName
            const transportTypePriceKey = Object.keys(transportTypePrices).find(key => input.InputFlowName.startsWith(key));
            if (transportTypePriceKey) {
                latestPriceValue = transportTypePrices[transportTypePriceKey];
            } else if (input.Hscode && input.Hscode.startsWith('2716')) {
                // Attempt to use electricityPrices based on DefaultProviderLocationCode or fallback to 'GLO'
                latestPriceValue = fetchElectricityPrice(input.DefaultProviderLocationCode) || fetchElectricityPrice('GLO');
            } else if (input.Hscode && input.Hscode.startsWith('2711')) {
                // Handle priceHistory for "2711" with specific unit adjustments
                const latestPriceHistory = fetchPriceHistory(input.Hscode);
                if (latestPriceHistory) {
                    let value = parseFloat(latestPriceHistory);
                    switch (input.UnitName) {
                        case "m3":
                            value *= 0.735;
                            break;
                        case "MJ":
                            value *= 0.020416667;
                            break;
                        // Add cases for other units if necessary
                    }
                    latestPriceValue = value.toString();
                }
            } else if (input.Hscode) {
                // Handle priceHistory for other cases
                // Adjusted to use the new fetchPriceHistory function
                latestPriceValue = fetchPriceHistory(input.Hscode);
            }

            // If a latest price value was found, set the localDollarPerUnit to it
            if (latestPriceValue) {
                setLocalDollarPerUnit(latestPriceValue);
            }
        }, [input.Hscode, input.DefaultProviderLocationCode, input.UnitName, input.InputFlowName, priceHistory, electricityPrices, selectedYear, selectedMonth]);



        useEffect(() => {
            const nodeTotalKey = `parent-${input?.InputFlowID?.low}-${input?.DefaultProvider?.low}`;
            console.log('nodeTotalKey:', nodeTotalKey);
            console.log('nodeTotals:', nodeTotals);
            const nodeTotalEntry = nodeTotals[nodeTotalKey]?.find(nt => nt.REFYEAR === selectedYear && nt.REFMONTH === selectedMonth) || { total: 0 };
            if (nodeTotalEntry.total) {
                setLocalDollarPerUnit(nodeTotalEntry.total.toString());
            } else {
                console.log(`Total not found for key: ${nodeTotalKey}`);
            }
        }, [nodeID, nodeTotals, selectedYear, selectedMonth]);


        useEffect(() => {
            if (localDollarPerUnit) {
                const newDollarValue = parseFloat(localDollarPerUnit);
                const uniqueKey = `${nodeID}-${input?.InputFlowID?.low}-${input?.DefaultProvider?.low}`;

                // Calculate new calculated total based on the new dollar value and input amount
                const newCalculatedTotal = newDollarValue * (input?.AmountValue || 1);

                // Constructing the DollarValue object including time dimensionality
                const dollarValueEntry = {
                    REFYEAR: selectedYear, // Assuming selectedYear is defined and holds the current year
                    REFMONTH: selectedMonth, // Assuming selectedMonth is defined and holds the current month (1-12)
                    dollarPerUnit: newDollarValue,
                    calculatedTotal: newCalculatedTotal,
                    ProductName: input.InputFlowName
                };

                // Update the global state with the new values
                setDollarValue(uniqueKey, dollarValueEntry);

                // Trigger recalculations as needed
                const TotalNodeKey = `${nodeID}`;
                calculateOverallTotal();
                calculateNodeTotal(TotalNodeKey, selectedYear, selectedMonth);

                // If there's a parent-child relationship, trigger updates
                const { parentId, subInputId } = parentChildMap[nodeID] || {};
                if (parentId && subInputId) {
                    updateParentSubInputValue(parentId, subInputId, newCalculatedTotal);
                }
            }
        }, [localDollarPerUnit, selectedYear, selectedMonth]);


        const handleDollarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setLocalDollarPerUnit(newValue); // Immediate feedback to user
        };

        // Example use of nodeTotals to possibly force a re-render or use in your JSX
        const nodeTotal = nodeTotals[nodeID] || 0;

        const dollarValueEntries = dollarValues[`${nodeID}-${input.InputFlowID.low}-${input.DefaultProvider.low}`] || [];
        const totalForSelectedPeriod = dollarValueEntries
            .filter(entry => entry.REFYEAR === selectedYear && entry.REFMONTH === selectedMonth)
            .reduce((acc, entry) => acc + entry.calculatedTotal, 0);





        return (
            <div
                key={key}
                style={{
                    ...style.io,
                    ...style.flexRow,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                    backgroundColor: isGrouped ? '#f0f0f0' : 'transparent',
                    paddingRight: isGrouped ? '0px' : '40px'
                }}
                onDoubleClick={() => onInputDoubleClick?.(input.InputFlowID.low, input.DefaultProvider.low, data.startingflowid, data.processid)}
            >
                <Tooltip title={input.HscodeName} placement="left">
                    <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                        <div style={{ flexGrow: 1 }}>

                            <div style={{ fontWeight: 'bold' }}>{input.InputFlowName}</div>

                            <div style={{ fontSize: 'small' }}>
                                <Chip label={input.DefaultProviderLocation ? input.DefaultProviderLocation : "Elemental"} variant="outlined" size="small" />
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div style={style.unitcard}>{input.UnitName}</div>
                            <div style={style.amountcard}>{Number(input.AmountValue.toFixed(4))}</div>
                            <div style={{ ...style.inputamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                <label style={{ fontSize: '0.7em', marginBottom: '2px' }}>$ per
                                    {input.UnitName}</label>
                                <input
                                    type="number"
                                    value={parseFloat(localDollarPerUnit).toFixed(4)}
                                    onChange={(e) => handleDollarChange(e)}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        textAlign: 'center',
                                        width: '100%',
                                        outline: 'none',
                                        fontSize: '0.8em',
                                    }}
                                />

                            </div>
                            <div style={{ ...style.subtotalamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                <label style={{ fontSize: '0.7em', marginBottom: '5px' }}>Total $ item</label>
                                <div style={{
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'center',
                                    width: '100%',
                                    outline: 'none',
                                    fontSize: '0.8em',
                                }}>
                                    ${totalForSelectedPeriod.toFixed(4)}
                                </div>
                            </div>
                        </div>
                    </div>
                </Tooltip>
                <Handle
                    type="target"
                    position={Position.Left}
                    id={`input-${id}-${input.InputFlowID.low}`}
                    style={{ ...style.handle, position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}
                />
            </div>
        );
    };

    const renderGroupedInputsUnderFlowType = (groupedInputs: GroupedInputs) => Object.entries(groupedInputs).map(([flowID, inputs]) => {


        // Calculate the subtotal for the grouped inputs
        const subtotal = inputs.reduce((acc, input) => {
            const uniqueKey = `${nodeID}-${input.InputFlowID.low}-${input.DefaultProvider.low}`;
            const dollarValueEntries = dollarValues[uniqueKey] || [];

            // Calculate the total for the selected period for this specific input
            const totalForSelectedPeriod = dollarValueEntries
                .filter(entry => entry.REFYEAR === selectedYear && entry.REFMONTH === selectedMonth)
                .reduce((sum, entry) => sum + entry.calculatedTotal, 0);

            return acc + totalForSelectedPeriod;
        }, 0);

        // Calculate the total amount of units for the group
        const totalAmount = inputs.reduce((sum, current) => sum + current.AmountValue, 0);
        const roundedTotalAmount = Number(totalAmount.toFixed(4));

        // Calculate the weighted average per unit (ensure not to divide by zero)
        const weightedAverage = roundedTotalAmount > 0 ? subtotal / roundedTotalAmount : 0;




        if (inputs.length > 1) {
            const firstInput = inputs[0];
            return (
                <Tooltip title={firstInput.HscodeName} placement="left">
                    <Accordion key={`group-${flowID}`}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ flexGrow: 1 }}>
                                    <div style={{ fontWeight: 'bold' }}>{firstInput.InputFlowName}</div>
                                    <div style={{ fontSize: 'small' }}> <Chip label="Multiple Origins" size="small" /></div>
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <div style={style.unitcard}>{firstInput.UnitName}</div>
                                    <div style={style.amountcard}>{roundedTotalAmount}</div>
                                    <div style={{ ...style.subtotalamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                        <label style={{ fontSize: '0.7em', marginBottom: '5px' }}>Avg.</label>
                                        <div style={{
                                            border: 'none', // Remove any default browser input border
                                            background: 'transparent', // Remove default background
                                            textAlign: 'center', // Center text
                                            width: '100%', // Use full width of the card
                                            outline: 'none', // Remove focus outline
                                            fontSize: '0.8em',
                                        }}>${weightedAverage.toFixed(4)}/{firstInput.UnitName}
                                        </div>
                                    </div>
                                    <div style={{ ...style.subtotalamountcard, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
                                        <label style={{ fontSize: '0.7em', marginBottom: '5px' }}>Total $ items</label>
                                        <div style={{
                                            border: 'none', // Remove any default browser input border
                                            background: 'transparent', // Remove default background
                                            textAlign: 'center', // Center text
                                            width: '100%', // Use full width of the card
                                            outline: 'none', // Remove focus outline
                                            fontSize: '0.8em',
                                        }}>$
                                            {subtotal.toFixed(4)}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </AccordionSummary>
                        <AccordionDetails style={{ backgroundColor: '#f0f0f0' }}>
                            {inputs.map((input, index) => RenderInput(input, `grouped-input-${flowID}-${index}`, true))}
                        </AccordionDetails>
                    </Accordion>
                </Tooltip>
            );
        } else {
            return RenderInput(inputs[0], `input-${flowID}`);
        }
    });

    // Adjust the render logic for displaying node-specific totals
    const nodeTotalEntry = nodeTotals[nodeID]?.find(nt => nt.REFYEAR === selectedYear && nt.REFMONTH === selectedMonth) || { total: 0 };
    const nodeSpecificTotal = nodeTotalEntry.total;



    const nodeContent = (
        <>

            <div style={style.contentHeader}>Inputs<ConstructionOutlinedIcon /></div>
            <>
                {Object.keys(organizedInputs.PRODUCT_FLOW).length > 0 && (
                    <div>
                        <div style={style.subcontentHeader}>Product Flows <CategoryOutlinedIcon /></div>
                        {renderGroupedInputsUnderFlowType(organizedInputs.PRODUCT_FLOW)}
                    </div>
                )}
                {Object.keys(organizedInputs.ELEMENTARY_FLOW).length > 0 && (
                    <div>
                        <div style={style.subcontentHeader}>Elementary Flows <ScienceOutlinedIcon /></div>
                        {renderGroupedInputsUnderFlowType(organizedInputs.ELEMENTARY_FLOW)}
                    </div>
                )}
            </>

            <div style={{ ...style.contentHeader, marginTop: '20px' }}>Outputs<Inventory2OutlinedIcon /></div>
            {data.outputs.map((output, index) => (
                <div key={`output-${index}`} style={{
                    ...style.io,
                    ...style.flexRow,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                }}>
                    <div style={{ flexGrow: 1 }}>
                        <div style={{ fontWeight: 'bold' }}>{output.OutputFlowName} </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div style={style.unitcard}>{output.UnitName}</div>
                        <div style={style.amountcard}> {output.AmountValue}</div>
                    </div>

                    <div style={style.totalamountcard}>
                        ${nodeTotalEntry.total.toFixed(4)}/{output.UnitName}
                    </div>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id={`output-${id}-${output.OutputFlowID.low}`}
                        style={{ ...style.handle, ...style.right }}
                    />
                </div>
            ))}


        </>
    );

    console.log(`Selected Year: ${selectedYear}, Selected Month: ${selectedMonth}`);

    // Helper function to convert URLs to clickable links
    const linkify = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split(urlRegex).map((part: string, index: number) => {
            if (urlRegex.test(part)) {
                return (
                    <a href={part} key={index} target="_blank" rel="noopener noreferrer" style={{ color: 'blue', textDecoration: 'underline' }}>
                        {part}
                    </a>
                );
            } else {
                return part;
            }
        });
    };

    const processDescription = (description: string) => {
        const [content, referencesRaw] = description.split("References:");
        let references = referencesRaw?.trim().split(/;|\.\s+/).filter((ref: string) => ref); // Splitting by semicolon or period followed by space

        return (
            <>
                <div>{content}</div>
                {references && (
                    <>
                        <div style={{ marginTop: '16px', fontWeight: 'bold' }}>References:</div>
                        {references.map((ref: string, index: number) => (
                            <div key={index} style={{ marginTop: '8px' }}>{linkify(ref)};</div>
                        ))}
                    </>
                )}
            </>
        );
    };


    const iconContainer = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <IconButton onClick={handlePopoverOpen} size="small" style={{ color: '"#05777F' }}>
                <InfoOutlinedIcon />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Typography style={{ padding: '20px', maxWidth: '800px', fontSize: '0.875rem' }}>
                    <div style={{ marginBottom: '16px', fontSize: '1rem', fontWeight: 'bold' }}>DETAILS OF PRODUCTION PROCESS</div>
                    <div><b>Technology:</b> {data.Technology}</div>
                    <div><b>Geography:</b> {data.Geography}</div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <b>Description:</b>
                        {processDescription(data.ProcessDescription)}
                    </div>
                </Typography>
            </Popover>
        </div>
    );




    return (
        <>
            {iconContainer} {/* Icon positioned above and independent of the Node */}
            <Node label={data.label} selected={selected} color="#0E303A" content={nodeContent} />
        </>
    );
};

export default GroupedNode;
